import { Breadcrumb, BreadcrumbItem } from "flowbite-react";
import { HiHome } from "react-icons/hi";
import { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getCookie } from "../../reducer/utils";
import { Chart } from "react-google-charts";
import moment from "moment";
import FutureOriginalCard from "./FutureOriginalCard";

function FutureOriginalAnalytics() {
  const baseURL = process.env.REACT_APP_BASE_URL;
  const toastId = useRef(null);

  const [dateRangeOption, setDateRangeOption] = useState("last_run");
  const [customDateRange, setCustomDateRange] = useState(() => {
    // Initialize with "last_run" values (API defaults)
    // Releases need 2 days to be marked as failed, so end = now - 2 days
    const now = moment.utc().startOf('day');
    return {
      start: now.clone().subtract(3, "days").subtract(1, "hour").format("YYYY-MM-DDTHH:mm:ss"),
      end: now.clone().subtract(2, "days").format("YYYY-MM-DDTHH:mm:ss"),
    };
  });

  const [analyticsData, setAnalyticsData] = useState({
    passed: [],
    failed: [],
    failedWithNotes: [],
  });

  const [loading, setLoading] = useState(false);
  
  // Modal state for showing FutureOriginalCard
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFutureOriginal, setSelectedFutureOriginal] = useState(null);
  const [fetchingFutureOriginal, setFetchingFutureOriginal] = useState(false);
  
  // Carousel navigation state
  const [currentCategory, setCurrentCategory] = useState(null); // "passed", "failedWithNotes", "failedWithoutNotes"
  const [currentIndex, setCurrentIndex] = useState(0);

  // Calculate date range based on option (independent of state)
  // Note: API filters are > start and < end (exclusive on both ends)
  // Releases are stored at 00:00 UTC and need 2 days to be marked as failed
  const calculateDateRange = (option) => {
    const now = moment.utc().startOf('day');
    
    switch (option) {
      case "last_run":
        // Default API behavior: check releases from 3 days ago (with 1hr overlap) until 2 days ago
        {
          const start = now.clone().subtract(3, "days").subtract(1, "hour").format("YYYY-MM-DDTHH:mm:ss");
          const end = now.clone().subtract(2, "days").format("YYYY-MM-DDTHH:mm:ss");
          return {
            start,
            end,
            displayStart: start,
            displayEnd: end,
          };
        }
      case "last_week":
        // Check releases from last week + 2 days (to allow failure detection) until 2 days ago
        {
          const start = now.clone().subtract(1, "week").subtract(2, "days").subtract(1, "hour").format("YYYY-MM-DDTHH:mm:ss");
          const end = now.clone().subtract(2, "days").format("YYYY-MM-DDTHH:mm:ss");
          return {
            start,
            end,
            displayStart: start,
            displayEnd: end,
          };
        }
      case "last_month":
        // Check releases from last month + 2 days (to allow failure detection) until 2 days ago
        {
          const start = now.clone().subtract(1, "month").subtract(2, "days").subtract(1, "hour").format("YYYY-MM-DDTHH:mm:ss");
          const end = now.clone().subtract(2, "days").format("YYYY-MM-DDTHH:mm:ss");
          return {
            start,
            end,
            displayStart: start,
            displayEnd: end,
          };
        }
      case "custom":
        // For custom range: subtract 1 day from start to include that date (due to > filter)
        // Add 1 day to end to include that date (due to < filter)
        {
          const start = moment.utc(customDateRange.start).format("YYYY-MM-DDTHH:mm:ss");
          const end = moment.utc(customDateRange.end).format("YYYY-MM-DDTHH:mm:ss");
          return {
            start,
            end,
            displayStart: start,
            displayEnd: end,
          };
        }
      default:
        {
          const start = now.clone().subtract(3, "days").subtract(1, "hour").format("YYYY-MM-DDTHH:mm:ss");
          const end = now.clone().subtract(2, "days").format("YYYY-MM-DDTHH:mm:ss");
          return {
            start,
            end,
            displayStart: start,
            displayEnd: end,
          };
        }
    }
  };

  const getAnalytics = async (option = null) => {
    setLoading(true);
    toastId.current = toast.loading("Fetching analytics...");

    try {
      const currentOption = option || dateRangeOption;
      const dateRange = calculateDateRange(currentOption);
      
      const params = new URLSearchParams({
        start: dateRange.start,
        end: dateRange.end,
        slack_trigger: "false", // Don't trigger slack alerts when viewing analytics
      });

      const response = await fetch(
        `${baseURL}/internal/api/v5/utility/futures-originals/alert?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${getCookie("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch analytics");
      }

      const data = await response.json();
      
      // API returns [passed_fos, failed_fos, failed_fos_with_notes]
      setAnalyticsData({
        passed: data[0] || [],
        failed: data[1] || [],
        failedWithNotes: data[2] || [],
      });

      toast.update(toastId.current, {
        render: "Analytics fetched successfully",
        type: "success",
        isLoading: false,
        autoClose: 1000,
      });
    } catch (error) {
      toast.update(toastId.current, {
        render: `Error: ${error.message}`,
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAnalytics();
  }, []);

  const handleDateRangeOptionChange = (option) => {
    setDateRangeOption(option);
    
    // Calculate and update display fields
    const dateRange = calculateDateRange(option);
    setCustomDateRange({
      start: dateRange.displayStart,
      end: dateRange.displayEnd,
    });
    
    // Call API immediately with the new option (don't wait for state)
    if (option !== "custom") {
      getAnalytics(option);
    }
  };

  const handleCustomDateChange = (field, value) => {
    // Normalize input (which is local, from datetime-local) to UTC string
    const utcValue = moment.utc(value).format("YYYY-MM-DDTHH:mm:ss");
    setCustomDateRange((prev) => ({
      ...prev,
      [field]: utcValue,
    }));
  };

  const handleApplyCustomRange = () => {
    getAnalytics();
  };

  // Function to fetch individual future original by ID
  const fetchFutureOriginal = async (id) => {
    setModalOpen(true);
    setFetchingFutureOriginal(true);
    try {
      const response = await fetch(
        `${baseURL}/api/v4/vod/future-originals/${id}?include_episode_status=true`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${getCookie("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch future original");
      }

      const data = await response.json();
      setSelectedFutureOriginal(data);
    } catch (error) {
      toast.error(`Error fetching future original: ${error.message}`);
    } finally {
      setFetchingFutureOriginal(false);
    }
  };

  // Get the list for a given category
  const getListForCategory = (category) => {
    switch (category) {
      case "passed":
        return analyticsData.passed;
      case "failedWithNotes":
        return analyticsData.failedWithNotes;
      case "failedWithoutNotes":
        return failedWithoutNotes;
      default:
        return [];
    }
  };

  // Handle clicking on an ID
  const handleIdClick = (id, category) => {
    const list = getListForCategory(category);
    const index = list.indexOf(id);
    setCurrentCategory(category);
    setCurrentIndex(index >= 0 ? index : 0);
    fetchFutureOriginal(id);
  };

  // Navigate to previous item in the current category
  const handlePrevious = () => {
    if (currentCategory && currentIndex > 0) {
      const list = getListForCategory(currentCategory);
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      fetchFutureOriginal(list[newIndex]);
    }
  };

  // Navigate to next item in the current category
  const handleNext = () => {
    if (currentCategory) {
      const list = getListForCategory(currentCategory);
      if (currentIndex < list.length - 1) {
        const newIndex = currentIndex + 1;
        setCurrentIndex(newIndex);
        fetchFutureOriginal(list[newIndex]);
      }
    }
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedFutureOriginal(null);
    setCurrentCategory(null);
    setCurrentIndex(0);
  };

  // Handle refreshing the future original data after edit
  const handleRefreshFutureOriginal = () => {
    if (selectedFutureOriginal) {
      fetchFutureOriginal(selectedFutureOriginal.id);
    }
  };

  // Calculate failed without notes (difference between all failed and failed with notes)
  const failedWithoutNotes = analyticsData.failed.filter(
    (id) => !analyticsData.failedWithNotes.includes(id)
  );

  // Prepare data for Google Charts
  const chartData = [
    ["Status", "Count"],
    ["Passed", analyticsData.passed.length],
    ["Failed (with notes)", analyticsData.failedWithNotes.length],
    ["Failed (needs attention)", failedWithoutNotes.length],
  ];

  const chartOptions = {
    title: "Future Originals Status",
    pieHole: 0.4, // Donut chart
    is3D: false,
    colors: ["#10B981", "#F59E0B", "#EF4444"], // Green for passed, Amber for failed with notes, Red for failed without notes
    legend: {
      position: "bottom",
      alignment: "center",
    },
    chartArea: { width: "90%", height: "70%" },
    pieSliceText: "value",
  };

  const totalCount = analyticsData.passed.length + analyticsData.failed.length;
  const passedPercentage =
    totalCount > 0
      ? ((analyticsData.passed.length / totalCount) * 100).toFixed(1)
      : 0;
  const failedWithNotesPercentage =
    totalCount > 0
      ? ((analyticsData.failedWithNotes.length / totalCount) * 100).toFixed(1)
      : 0;
  const failedWithoutNotesPercentage =
    totalCount > 0
      ? ((failedWithoutNotes.length / totalCount) * 100).toFixed(1)
      : 0;

  return (
    <>
      <ToastContainer />

      <div className="flex items-center justify-between rounded-lg border-2 border-solid border-grey1 bg-white px-4 py-4">
        <Breadcrumb aria-label="Default breadcrumb example">
          <BreadcrumbItem href="/dashboard" icon={HiHome} />
          <BreadcrumbItem href="/future-originals">Future Originals</BreadcrumbItem>
          <BreadcrumbItem className="text-primary1">Analytics</BreadcrumbItem>
        </Breadcrumb>
      </div>

      <div className="mt-4 rounded-lg bg-white p-6 shadow-md">
        <div className="mb-6">
          <h2 className="mb-4 text-lg font-semibold">Future Originals Analytics</h2>
          
           {/* Date Range Filter */}
           <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
             <h3 className="mb-3 text-sm font-medium text-gray-700">
               Release Date Range Filter (UTC)
             </h3>
             <p className="mb-3 text-xs text-gray-500">
               Note: Releases need 2 days after their release date to be marked as failed
             <br />
               How it works: Retrieves all 'en' releases between these dates (exclusive of both). A release is marked as failed if any episode of the given FO is not in the Completed state.
             </p>
             <div className="flex flex-wrap items-end gap-4">
               <div className="flex-1 min-w-[200px]">
                 <label className="mb-1 block text-xs font-medium text-gray-600">
                   Time Period
                 </label>
                 <select
                   value={dateRangeOption}
                   onChange={(e) => handleDateRangeOptionChange(e.target.value)}
                   className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary1 focus:outline-none focus:ring-1 focus:ring-primary1"
                   disabled={loading}
                 >
                   <option value="last_run">Last Run (3 days ago)</option>
                   <option value="last_week">Last Week</option>
                   <option value="last_month">Last Month</option>
                   <option value="custom">Custom Range</option>
                 </select>
               </div>

              <div className="flex-1 min-w-[200px]">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Start Date-Time (UTC)
                </label>
                <input
                  type="datetime-local"
                  step="1"
                  value={customDateRange.start}
                  onChange={(e) => handleCustomDateChange("start", e.target.value)}
                  readOnly={dateRangeOption !== "custom"}
                  className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary1 focus:outline-none focus:ring-1 focus:ring-primary1 ${
                    dateRangeOption !== "custom" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  End Date-Time (UTC)
                </label>
                <input
                  type="datetime-local"
                  step="1"
                  value={customDateRange.end}
                  onChange={(e) => handleCustomDateChange("end", e.target.value)}
                  readOnly={dateRangeOption !== "custom"}
                  className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary1 focus:outline-none focus:ring-1 focus:ring-primary1 ${
                    dateRangeOption !== "custom" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>
               
               {dateRangeOption === "custom" && (
                 <button
                   onClick={handleApplyCustomRange}
                   disabled={loading}
                   className="rounded-md bg-primary1 px-6 py-2 text-sm font-medium text-white hover:bg-primary1/90 disabled:opacity-50"
                 >
                   Apply
                 </button>
               )}
             </div>
           </div>

          {/* Statistics Cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">Total</div>
              <div className="mt-2 text-3xl font-bold text-gray-900">{totalCount}</div>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-green-600">Passed</div>
              <div className="mt-2 flex items-end justify-between">
                <div className="text-3xl font-bold text-green-700">
                  {analyticsData.passed.length}
                </div>
                <div className="text-sm font-semibold text-green-600">
                  {passedPercentage}%
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-amber-600">Failed (with notes)</div>
              <div className="mt-2 flex items-end justify-between">
                <div className="text-3xl font-bold text-amber-700">
                  {analyticsData.failedWithNotes.length}
                </div>
                <div className="text-sm font-semibold text-amber-600">
                  {failedWithNotesPercentage}%
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-red-600">Failed (needs attention)</div>
              <div className="mt-2 flex items-end justify-between">
                <div className="text-3xl font-bold text-red-700">
                  {failedWithoutNotes.length}
                </div>
                <div className="text-sm font-semibold text-red-600">
                  {failedWithoutNotesPercentage}%
                </div>
              </div>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6">
            {totalCount > 0 ? (
              <Chart
                chartType="PieChart"
                data={chartData}
                options={chartOptions}
                width="100%"
                height="400px"
              />
            ) : (
              <div className="flex h-[400px] items-center justify-center text-gray-500">
                No data available for the selected date range
              </div>
            )}
          </div>

          {/* Optional: Lists of IDs */}
          {totalCount > 0 && (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-lg border border-green-200 bg-white p-4">
                <h3 className="mb-3 font-semibold text-green-700">
                  Passed IDs ({analyticsData.passed.length})
                </h3>
                <div className="max-h-60 overflow-y-auto rounded bg-green-50 p-3">
                  {analyticsData.passed.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {analyticsData.passed.map((id) => (
                        <span
                          key={id}
                          className="cursor-pointer rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-200 transition-colors"
                          onClick={() => handleIdClick(id, "passed")}
                          title="Click to view details"
                        >
                          {id}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No passed IDs</div>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-white p-4">
                <h3 className="mb-3 font-semibold text-amber-700">
                  Failed IDs (with notes) ({analyticsData.failedWithNotes.length})
                </h3>
                <div className="max-h-60 overflow-y-auto rounded bg-amber-50 p-3">
                  {analyticsData.failedWithNotes.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {analyticsData.failedWithNotes.map((id) => (
                        <span
                          key={id}
                          className="cursor-pointer rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-200 transition-colors"
                          onClick={() => handleIdClick(id, "failedWithNotes")}
                          title="Click to view details"
                        >
                          {id}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No failed IDs with notes</div>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-red-200 bg-white p-4">
                <h3 className="mb-3 font-semibold text-red-700">
                  Failed IDs (needs attention) ({failedWithoutNotes.length})
                </h3>
                <div className="max-h-60 overflow-y-auto rounded bg-red-50 p-3">
                  {failedWithoutNotes.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {failedWithoutNotes.map((id) => (
                        <span
                          key={id}
                          className="cursor-pointer rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-200 transition-colors"
                          onClick={() => handleIdClick(id, "failedWithoutNotes")}
                          title="Click to view details"
                        >
                          {id}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No failed IDs without notes</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal for FutureOriginalCard */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={handleCloseModal}
          ></div>
          
          {/* Left Arrow Button */}
          {currentCategory && (
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0 || fetchingFutureOriginal}
              className={`absolute left-4 z-50 rounded-full bg-white p-3 shadow-lg transition-all ${
                currentIndex === 0 || fetchingFutureOriginal
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-100 hover:scale-110"
              }`}
              title="Previous"
            >
              <svg
                className="h-6 w-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}

          {/* Right Arrow Button */}
          {currentCategory && (
            <button
              onClick={handleNext}
              disabled={currentIndex >= getListForCategory(currentCategory).length - 1 || fetchingFutureOriginal}
              className={`absolute right-4 z-50 rounded-full bg-white p-3 shadow-lg transition-all ${
                currentIndex >= getListForCategory(currentCategory).length - 1 || fetchingFutureOriginal
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-100 hover:scale-110"
              }`}
              title="Next"
            >
              <svg
                className="h-6 w-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}
          
          {/* Modal Content */}
          <div className="relative max-h-[90vh] w-full max-w-6xl overflow-auto rounded-lg bg-white p-6 shadow-xl mx-4">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute right-4 top-4 z-10 rounded-full bg-gray-100 p-2 hover:bg-gray-200 transition-colors"
              title="Close"
            >
              <svg
                className="h-5 w-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Position Indicator */}
            {currentCategory && (
              <div className="absolute left-4 top-4 z-10 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600">
                {currentIndex + 1} of {getListForCategory(currentCategory).length}
              </div>
            )}

            {/* Loading State */}
            {fetchingFutureOriginal && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary1 mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading future original...</p>
                </div>
              </div>
            )}

            {/* FutureOriginalCard */}
            {selectedFutureOriginal && !fetchingFutureOriginal && (
              <div className="pr-8">
                <FutureOriginalCard 
                  futureOriginal={selectedFutureOriginal}
                  getFutureOriginal={handleRefreshFutureOriginal}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default FutureOriginalAnalytics;
